import { useStore } from '@nanostores/react';
import { formatDate } from 'date-fns';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Button } from '~/components/ui/Button';
import { Input } from '~/components/ui/Input';
import { LoadingDots } from '~/components/ui/LoadingDots';
import { Switch } from '~/components/ui/Switch';
import { WINDOW_RELOAD_TIMEOUT } from '~/lib/constants';
import {
  getStripeOrgSubscriptionInfo,
  getStripePersonalSubscriptionInfo,
  updatePersonalSubscription,
  updateTeamSubscription,
} from '~/lib/membership/membershipApi';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import type { Coupon } from '~/types/coupons';
import type { StripeSubscriptionSession } from '~/types/subscription';
import { withSpinner } from '~/utils/withSpinner';

const getFormattedDiscountValue = (coupon: Coupon) => {
  return typeof coupon.couponAmountOff === 'number'
    ? `$${coupon.couponAmountOff / 100}`
    : `${coupon.couponPercentOff || 0}%`;
};

export const Summary = () => {
  const [userPromoCode, setUserPromoCode] = useState('');
  const [invoiceData, setInvoiceData] = useState<StripeSubscriptionSession | null>(null);

  const [isConfirming, setIsConfirming] = useState(false);
  const [isInvoiceLoading, setIsInvoiceLoading] = useState(true);

  const updatePlanScreenData = useStore(pricingStore.updatePlanScreenData);
  const promotionCodeData = invoiceData?.promotionCodeData;
  const isYearlyPrice = updatePlanScreenData?.interval === 'yearly';

  useEffect(() => {
    updateSubscriptionInfo(undefined);
  }, [isYearlyPrice]);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!updatePlanScreenData) {
    return null;
  }

  const switchIsYearlyPrice = (isYearly: boolean) => {
    pricingStore.updatePlanScreenData.set({
      ...updatePlanScreenData,
      interval: isYearly ? 'yearly' : 'monthly',
    });
  };

  const handleConfirm = async () => {
    setIsConfirming(true);

    const handleSuccessfulUpdate = () => {
      toast.success('Subscription was successfully updated');
      setTimeout(() => {
        window.location.reload();
      }, WINDOW_RELOAD_TIMEOUT);
    };

    try {
      if (updatePlanScreenData.organization) {
        await updateTeamSubscription(
          updatePlanScreenData.organization,
          updatePlanScreenData.pricing.level,
          updatePlanScreenData.interval,
          userPromoCode,
        );
      } else {
        await updatePersonalSubscription(
          updatePlanScreenData.pricing.level,
          updatePlanScreenData.interval,
          userPromoCode,
        );
      }

      handleSuccessfulUpdate();
    } catch (error: any) {
      if (error.status === 402) {
        window.location.href = error.redirectToUrl;
        return;
      } else {
        toast.error(error.message || 'Error while updating subscription');
      }

      setIsConfirming(false);
    }
  };

  const updateSubscriptionInfo = (promocode?: string) => {
    withSpinner({
      action: async () => {
        const response = updatePlanScreenData.organization
          ? await getStripeOrgSubscriptionInfo(
              updatePlanScreenData.organization,
              updatePlanScreenData.pricing.level,
              updatePlanScreenData.interval,
              promocode ? promocode : undefined,
            )
          : await getStripePersonalSubscriptionInfo(
              updatePlanScreenData.pricing.level,
              updatePlanScreenData.interval,
              promocode ? promocode : undefined,
            );

        if ('data' in response) {
          setInvoiceData(response.data);
          setErrorMessage(null);

          if (response.data.promotionCodeData.error) {
            toast.error(response.data.promotionCodeData.error);
          }
        } else {
          setErrorMessage(response.error);
        }
      },
      onStateUpdate: setIsInvoiceLoading,
    });
  };

  const discardPromoCode = () => {
    setUserPromoCode('');
    updateSubscriptionInfo(undefined);
  };

  const getUpdateType = () => {
    const typeMap = {
      upgrade: 'upgrade',
      downgrade: 'downgrade',
      change_interval: 'changing interval',
    };

    return typeMap[updatePlanScreenData.updateType] || 'update';
  };

  if (!updatePlanScreenData) {
    return null;
  }

  return (
    <div className="pl-0 sm:pl-8 text-bolt-ds-textPrimary flex-1 flex flex-col gap-4 sm:border-l sm:border-l-bolt-ds-borderOutline">
      {isInvoiceLoading ? (
        <LoadingDots text="Loading your order summary" />
      ) : errorMessage ? (
        <div className="flex justify-center items-center h-full">{errorMessage}</div>
      ) : !invoiceData ? (
        <div className="flex justify-center items-center h-full">
          Sorry, we couldn't get your subscription payment details. Please try again later or contact our support team.
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xl font-semibold mb-2">Your {getUpdateType()} summary</div>
            {updatePlanScreenData.updateType !== 'change_interval' && (
              <div className="flex items-center gap-2 grow-1 justify-end">
                <label htmlFor="interval-switch" className="text-bolt-elements-textSecondary text-sm text-nowrap">
                  Annual billing
                </label>
                <Switch id="interval-switch" size="sm" checked={isYearlyPrice} onCheckedChange={switchIsYearlyPrice} />
              </div>
            )}
          </div>
          <div className="flex flex-col gap-4">
            <div className="flex justify-between gap-3">
              <div className="text-bolt-ds-textSecondary max-w-2/3">What you will pay now</div>
              <div>${(invoiceData.invoiceAmount ? invoiceData.invoiceAmount / 100 : 0).toFixed(2)}</div>
            </div>
            <div className="flex justify-between gap-3">
              <div className="text-bolt-ds-textSecondary max-w-2/3">
                What you will pay {isYearlyPrice ? 'yearly' : 'monthly'} starting{' '}
                {formatDate(invoiceData.billingCycleAnchor, 'MMMM\u00A0do,\u00A0yyyy')}
              </div>
              <div>
                $
                {isYearlyPrice
                  ? (updatePlanScreenData.pricing.rateMonthly.year * 12 * invoiceData.quantity).toFixed(2)
                  : (updatePlanScreenData.pricing.rateMonthly.month * invoiceData.quantity).toFixed(2)}
              </div>
            </div>
          </div>

          {promotionCodeData?.couponName ? (
            <div className="flex flex-col gap-3 text-sm text-bolt-ds-textPrimary items-start">
              <span className="flex justify-between gap-2">
                <span>Applied promocode</span>
                <b>{promotionCodeData.couponName}</b>
              </span>
              <span className="flex items-center justify-between gap-2">
                <span>Discount:</span>
                <b>{getFormattedDiscountValue(promotionCodeData)}</b>
              </span>
              <Button variant="link-color" onClick={discardPromoCode}>
                Change
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-2 items-start">
              <Input
                label="Promo code (optional)"
                className="w-full mt-3"
                placeholder="Enter your promo code"
                value={userPromoCode}
                onChange={(event) => {
                  setUserPromoCode(event.target.value);
                }}
                onKeyUp={(e) => {
                  if (e.key === 'Enter' && userPromoCode.length) {
                    updateSubscriptionInfo(userPromoCode);
                  }
                }}
              />
              {userPromoCode.length ? (
                <Button
                  onClick={() => {
                    updateSubscriptionInfo(userPromoCode);
                  }}
                  variant="link-color"
                >
                  Apply
                </Button>
              ) : null}
            </div>
          )}

          <div className="mt-auto text-bolt-ds-textSecondary text-center">
            By confirming your new plan, you agree to Bolt’s{' '}
            <a href="https://stackblitz.com/terms-of-service" target="_blank" className="underline">
              Terms of service
            </a>{' '}
            and{' '}
            <a href="https://stackblitz.com/privacy-policy" target="_blank" className="underline">
              Privacy policy
            </a>
          </div>
          <Button disabled={isConfirming} loading={isConfirming} onClick={handleConfirm}>
            Confirm {getUpdateType()}
          </Button>
        </>
      )}
    </div>
  );
};
